/**
 * Kitchen tablets: enrol, re-enrol, revoke, and the legacy IP-only switch (HMH-2417).
 *
 * A tablet is enrolled once: add it here, then type the one-time code on the
 * tablet at /kitchen/enroll within 30 minutes. The tablet keeps a device secret
 * in a cookie and signs itself in from then on.
 */

import { useState } from "react";
import {
  EnrolmentCodeResponse,
  KitchenDevice,
  useCreateKitchenDevice,
  useKitchenDevices,
  useNewEnrolmentCode,
  useRevokeKitchenDevice,
  useSetLegacyIpLogin,
} from "../hooks/useKitchenDevices";

function when(value: string | null): string {
  return value ? new Date(value).toLocaleString() : "Never";
}

function status(device: KitchenDevice): { label: string; cls: string } {
  if (device.enrolled) return { label: "Enrolled", cls: "bg-green-100 text-green-800" };
  if (device.code_pending) return { label: "Waiting for code", cls: "bg-yellow-100 text-yellow-800" };
  return { label: "Code expired", cls: "bg-gray-100 text-gray-800" };
}

export function KitchenTabletsPanel() {
  const { data, isLoading, error } = useKitchenDevices();
  const createMutation = useCreateKitchenDevice();
  const codeMutation = useNewEnrolmentCode();
  const revokeMutation = useRevokeKitchenDevice();
  const legacyMutation = useSetLegacyIpLogin();

  const [name, setName] = useState("");
  const [issued, setIssued] = useState<EnrolmentCodeResponse | null>(null);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setIssued(await createMutation.mutateAsync(name.trim()));
    setName("");
  };

  const handleNewCode = async (device: KitchenDevice) => {
    if (device.enrolled && !confirm(`Re-enrol ${device.name}? Its current sign-in keeps working until the new code is used.`)) {
      return;
    }
    setIssued(await codeMutation.mutateAsync(device.id));
  };

  const handleRevoke = async (device: KitchenDevice) => {
    if (!confirm(`Revoke ${device.name}? It is signed out at once and needs a new enrolment to come back.`)) {
      return;
    }
    await revokeMutation.mutateAsync(device.id);
  };

  const handleLegacy = async (enabled: boolean) => {
    const msg = enabled
      ? "Turn the IP-only login back on? Any device on the hospital network can then open the kitchen display."
      : "Turn the IP-only login off? Tablets that are not enrolled will show the PIN screen.";
    if (!confirm(msg)) return;
    await legacyMutation.mutateAsync(enabled);
  };

  return (
    <div className="bg-white rounded-lg shadow mt-6">
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-xl font-bold text-gray-900">Kitchen Tablets</h2>
        <p className="text-sm text-gray-600 mt-1">
          Each tablet is enrolled once with a one-time code, then signs itself in. Revoke a lost or replaced tablet here.
        </p>
      </div>

      <div className="p-6 space-y-6">
        {isLoading && <p className="text-gray-600">Loading tablets...</p>}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
            Error loading tablets: {error instanceof Error ? error.message : "Unknown error"}
          </div>
        )}

        {data && (
          <div
            className={`rounded-lg border p-4 flex items-center justify-between gap-4 ${
              data.legacy_ip_login ? "bg-yellow-50 border-yellow-300" : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="text-sm">
              <p className="font-medium text-gray-900">
                IP-only tablet login (legacy): {data.legacy_ip_login ? "ON" : "OFF"}
              </p>
              <p className="text-gray-600">
                {data.legacy_ip_login
                  ? "Any device behind the hospital's internet address gets the kitchen display. Turn this off once every tablet below is enrolled."
                  : "Only enrolled tablets (or a kitchen PIN) open the kitchen display."}
              </p>
            </div>
            <button
              onClick={() => handleLegacy(!data.legacy_ip_login)}
              disabled={legacyMutation.isPending}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap disabled:bg-gray-300 ${
                data.legacy_ip_login ? "bg-blue-600 text-white hover:bg-blue-700" : "bg-gray-200 text-gray-800 hover:bg-gray-300"
              }`}
            >
              {data.legacy_ip_login ? "Turn off" : "Turn back on"}
            </button>
          </div>
        )}

        <form onSubmit={handleAdd} className="flex gap-2">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            placeholder="Tablet name, e.g. Grill line"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="submit"
            disabled={createMutation.isPending || !name.trim()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
          >
            Add tablet
          </button>
        </form>

        {data && data.devices.length === 0 && <p className="text-sm text-gray-600">No tablets enrolled yet.</p>}

        {data && data.devices.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tablet</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Enrolled</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Last seen</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.devices.map((device) => {
                  const s = status(device);
                  return (
                    <tr key={device.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium text-gray-900">{device.name}</td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${s.cls}`}>{s.label}</span>
                      </td>
                      <td className="px-4 py-2 text-gray-600">{when(device.enrolled_at)}</td>
                      <td className="px-4 py-2 text-gray-600">{when(device.last_seen_at)}</td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        <button onClick={() => handleNewCode(device)} className="text-blue-600 hover:text-blue-900 mr-4">
                          New code
                        </button>
                        <button onClick={() => handleRevoke(device)} className="text-red-600 hover:text-red-900">
                          Revoke
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {issued && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Enrolment code for {issued.device.name}</h2>
            <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-6 mb-4">
              <p className="text-sm text-gray-700 mb-2 font-medium">
                On the tablet, open <span className="font-mono">/kitchen/enroll</span> and type this code within{" "}
                {issued.expires_in_minutes} minutes. It works once and is shown only now.
              </p>
              <div className="bg-white rounded-lg p-4 text-center">
                <p className="text-3xl font-mono font-bold text-gray-900 tracking-widest">{issued.enrolment_code}</p>
              </div>
            </div>
            <button
              onClick={() => setIssued(null)}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
